import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Meta, Footer } from '$partials/global/index.js';
import { NavigationPageWrapper, NavigationButton } from '$partials/navigation/index.js';
import { Project, ProjectNavigation } from '$partials/projectDetailPage/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * The project detail page, which allows the user to scroll through all projects
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ProjectDetailPage = (props) => {
    const [_status, setStatus] = useState(false);
    const [firstVisibleIndex, setFirstVisibleIndex] = useState(0);
    const [lastVisibleIndex, setLastVisibleIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [projects, setProjects] = useState(props?.data?.page ? [props?.data?.page] : []);

    const controller = useRef();
    const observerRef = useRef();
    const projectRefs = useRef([]);
    const visibilityRef = useRef(props?.data?.page ? { 0: true } : {});

    // Load next project
    const loadNextProject = () => {
        if (projects[projects.length - 1]?.nextProject &&
            projects[projects.length - 1].nextProject.id !== props?.data?.page?.id) {

            const nextProject = projects[projects.length - 1].nextProject;

            (loading && controller.current) && controller.current.abort();

            (async () => {
                try {
                    setLoading(true);

                    let signal;
                    if (window && window.AbortController) {
                        controller.current = new window.AbortController();
                        signal = controller.current.signal;
                    }

                    const response = await fetch(`/api/v1/projects/${nextProject.id}`, { signal: signal });

                    if (response) {
                        if (response.status !== 200) {
                            setLoading(false);
                            setStatus(response.status);
                            return;
                        }

                        const data = await response.json();

                        setProjects([...projects, ...[data]]);
                    }
                } catch (err) {
                    if (err instanceof DOMException && err.name === 'AbortError') { return; }

                    console.error(err);
                    setStatus(500);
                }
            })();
        }
    };

    // Setup intersection observer
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            /**
             * Determine first and last visible project indexes
             * @param {Array} entries
             */
            (entries) => {
                entries.forEach((e) => {
                    const index = projectRefs.current.findIndex((r) => r === e.target);

                    if (index >= 0) {
                        visibilityRef.current = {
                            ...visibilityRef.current,
                            [index]: e.isIntersecting
                        };

                        setFirstVisibleIndex(Object.keys(visibilityRef.current)
                            .filter((k) => visibilityRef.current[k] === true)
                            .sort()[0]);

                        setLastVisibleIndex(Object.keys(visibilityRef.current)
                            .filter((k) => visibilityRef.current[k] === true)
                            .sort()
                            .reverse()[0]);
                    }
                });
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0
            }
        );

        projectRefs.current.forEach((r) => observerRef.current.observe(r));
    }, []);

    // Observe new projects
    useEffect(() => {
        projectRefs.current.forEach((r) => observerRef.current.observe(r));
    }, [projects]);

    // Determine current project and set URL
    useEffect(() => {
        const currentProject = projects?.[firstVisibleIndex];

        if (currentProject?.url) {
            window.history.replaceState({}, '', projects[firstVisibleIndex].url);
        }

        if (Number(firstVisibleIndex) === (projects.length - 1)) {
            loadNextProject();
        }
    }, [firstVisibleIndex]);

    return (
        <>
            <Meta page={props.data.page} />

            <NavigationPageWrapper {...props.data?.navigation}>
                <main
                    className='pt-1'
                    id='main-content'>
                    {projects.map((project, i) => {
                        const isCurrentProject = (projects.length === 1 || i === Number(firstVisibleIndex));

                        return (
                            <Project
                                {...project}
                                categories={project.tags}
                                fixedPosition={i === (lastVisibleIndex - 1)}
                                isCurrentProject={isCurrentProject}
                                isFirst={i === 0}
                                isVisible={i >= firstVisibleIndex && i <= lastVisibleIndex}
                                key={`${project.id}-${i}`}
                                onProgressChange={(p) => {
                                    if (isCurrentProject) {
                                        setProgress(p);
                                    }
                                }}
                                ref={(element) => projectRefs.current[i] = element} />
                        );
                    })}

                    {firstVisibleIndex &&
                        <ProjectNavigation
                            allProjects={props?.data?.page?.allProjects?.edges?.map((edge) => edge.node)}
                            currentProject={firstVisibleIndex >= 0 && projects[firstVisibleIndex]}
                            progress={progress} />
                    }

                    <NavigationButton />

                    <div className='relative px-1 pb-1 bg-background'>
                        <Footer
                            mailchimpId={props?.data?.mailchimpId?.mailchimpId}
                            {...props?.data?.footer} />
                    </div>
                </main>
            </NavigationPageWrapper>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const projectPropTypes = {
    backgroundColour: PropTypes.string,
    categories: PropTypes.object,
    filterColour: PropTypes.string,
    foregroundColour: PropTypes.string,
    grid: PropTypes.array,
    id: PropTypes.string,
    isCurrentProject: PropTypes.bool,
    onProgressChange: PropTypes.func,
    onVisibilityChange: PropTypes.func,
    parent: PropTypes.object,
    projectCopy: PropTypes.string,
    projectDisplayTitle: PropTypes.string,
    title: PropTypes.string,
};

Project.propTypes = projectPropTypes;

const projectNavigationPropTypes = {
    currentProject: PropTypes.shape({
        title: PropTypes.string
    }),
    progress: PropTypes.number
};

ProjectNavigation.propTypes = projectNavigationPropTypes;

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        mailchimpId: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ProjectDetailPage.propTypes = propTypes;

export default ProjectDetailPage;
